import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { ConfigurationManagerService } from './configuration-manager.service';
import { Observable, Subject, map, of } from 'rxjs';
import { IncidentCategory, OrganizationIncidentCategoryWithCategoryResponse } from '../models/incident-category.model';
import { CoreService } from './core.service';
import { FocalPoint } from '../models/fixed-perimeter.models';
import { PinActivationDialogData } from '../interfaces/pin-activation.types';
import { GlobalSettingsService } from './global-settings.service';
import { OrganizationUtilService } from './organization-util.service';
import { switchMap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { SessionOptionsService } from './session-option.service';
import { SettingsService } from './settings.service';
import { ArcAngelMonitorModeService } from '../../components/shared/arc-angel-monitor-mode.service';
import { IllumLiveService } from './illum-live.service';

export interface FixedPerimeterCreationOptions {
  organizationId: string;
  basePerimeterId: string;
  label: string;
  description?: string;
  includeFloors: boolean;
  incidentCategoryId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class IncidentCategoryService {
  constructor(
    private api: ApiService,
    private core: CoreService,
    private GlobalSettings: GlobalSettingsService,
    private organizationUtilService: OrganizationUtilService,
    private sessionOptionService: SessionOptionsService,
    private settingsService: SettingsService,
    private illumLiveService: IllumLiveService,
    private arcAngelMonitorModeService: ArcAngelMonitorModeService
  ) { 

  }
private creations: Record<number, FixedPerimeterCreationOptions> = {};
  private counter = 0;
  private closedFixedPerimCreatorSubject = new Subject<void>();
  private creationRequestedSource = new Subject<number>();
  creationRequested$ = this.creationRequestedSource.asObservable();
  closedFixedPerimCreator$ = this.closedFixedPerimCreatorSubject.asObservable();
  private useDefaultCameraSubject = new Subject<void>();
  private showDataEntryViewSubject = new Subject<{ creationId: number }>();


  useDefaultCamera$ = this.useDefaultCameraSubject.asObservable();
  showDataEntryView$ = this.showDataEntryViewSubject.asObservable();
  private _verifiedPin?: string;
  
  private _managingOrgId: any;
 resetManagingOrgId(): void {
    this._managingOrgId = undefined;
  }

  setManagingOrgId(n: any): void {
    this._managingOrgId = n;
  }
 startFixedPerimeterCreation(
    organizationId: string,
    basePerimeterId: string,
    label: string,
    description?: string,
    includeFloors: boolean = true,
    incidentCategoryId: number = 0
  ): void {
    this.useDefaultCameraSubject.next();
    this.core.clearTrackingEntity();

    const id = this.counter++;
    this.creations[id] = {
      organizationId,
      basePerimeterId,
      label,
      description,
      includeFloors,
      incidentCategoryId
    };

    this.showDataEntryViewSubject.next({ creationId: id });
  }

  getFixedPerimeterCreationOptions(id: number): FixedPerimeterCreationOptions | undefined {
    return this.creations[id];
  }
  
  getIncidentCategoryForBuilding(): Observable<Record<string, IncidentCategory[]>> {
    const url = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.FindAllPanicButtonsWithIncidentCategory;
    const body = {
      order: [['order', 'ASC']]
    };
    return this.api.post<any>(url, body).pipe(
      map((response: any) => {
        return this.organizationUtilService.transformOrgIncCatWithIncCats(response);
      })
    );
  }
   notifyClosedFixedPerimCreator(): void {
    this.closedFixedPerimCreatorSubject.next();
  }

  createIncidentFromPerimeter(obj: {
    orgId: string;
    perimeterId: string;
    label: string;
    description?: string;
    includeFloors: boolean;
    focalPoint?: FocalPoint;
    incidentCategoryId?: string | number;
    selectedFloorPerimeterId?: string;
    selectedRoomPerimeterId?: string;
    alertId?: string;
  }): Observable<PinActivationDialogData> {
    let {
      orgId,
      perimeterId,
      selectedFloorPerimeterId,
      selectedRoomPerimeterId,
      alertId,
      description,
      focalPoint,
      incidentCategoryId,
      label
    } = obj;

    if (!description && perimeterId && this.organizationUtilService.data.myPerimeters?.[perimeterId]) {
      description = 'Incident Reported at ' + this.organizationUtilService.data.myPerimeters[perimeterId].name;
    }

    const options: PinActivationDialogData = {
      type: 'ACTIVATE_INCIDENT',
      orgIncidentCategoryId: incidentCategoryId ? String(incidentCategoryId) : undefined,
      perimeterId,
      selectedFloorPerimeterId,
      selectedRoomPerimeterId,
      alertId,
      focalPoint
    };

    this.GlobalSettings.setPerimeterIdForIncidentCreation(perimeterId);
    this.GlobalSettings.setOriginalOrgIdForIncidentCreation(orgId);

    return of(options);
  }

  createIncidentAfterDialog(): Observable<string | undefined> {
    const incidentOptions = this.GlobalSettings.getIncidentOptions();
    let typeLabel = incidentOptions.public ? ' [public]' : ' [private]';

    const papiIncident = {
        description: incidentOptions.description,
        name: incidentOptions.label + typeLabel,
        organizationIds: [this._managingOrgId],
        perimeterIds: [incidentOptions.perimeterId],
        focalPoint: incidentOptions.focalPoint,
        organizationIncidentCategoryId: incidentOptions.orgIncidentCategoryId ?? null,
        alertId: incidentOptions.alertId
    };

    const apiToCall = incidentOptions.public
      ? ConfigurationManagerService.settings.CreatePerimeterPublic
      : ConfigurationManagerService.settings.CreatePerimeter;

    const url = ConfigurationManagerService.settings.BaseURL + apiToCall;

    return this.api.post<any>(url, papiIncident).pipe(
      switchMap((response: any) =>
        this.updateStatusOfEvent(response.id, 'active').pipe(
          map((incident: any) => {
            if (!incident) return undefined;
            const orgId = this.arcAngelMonitorModeService.getOrgIdForLiveIncident(incident.id ?? '');
            this.illumLiveService.setManagingOrgId(orgId ?? this._managingOrgId);
            this.setManagingOrgId(orgId ?? this._managingOrgId);
            this.sessionOptionService.goToLiveIncidentMode(incident.id, true, true);

            return incident.id;
          })
        )
      ),
      catchError((error) => {
        console.error('Failed to create incident from perimeter.', error);
        return throwError(() => error);
      })
    );
  }

  createCampusIncidentAfterDialog(): Observable<string | undefined> {
    const incidentOptions = this.GlobalSettings.getIncidentOptions();
    let typeLabel = incidentOptions.public ? ' [public]' : ' [private]';
    const papiIncident = {
        description: incidentOptions.description,
        name: incidentOptions.label + typeLabel,
        organizationIds: Array.isArray(this._managingOrgId)
          ? this._managingOrgId
          : [this._managingOrgId],
        perimeterIds: incidentOptions.perimeterId,
        focalPoint: incidentOptions.focalPoint,
        organizationIncidentCategoryId: incidentOptions.orgIncidentCategoryId ?? null,
        alertId: incidentOptions.alertId
    };

    const apiToCall = incidentOptions.public
      ? ConfigurationManagerService.settings.CreateCampusPerimeterPublic
      : ConfigurationManagerService.settings.CreateCampusPerimeter;

    const url = ConfigurationManagerService.settings.BaseURL + apiToCall;

    return this.api.post<any>(url, papiIncident).pipe(
      switchMap((response: any) =>
        this.updateStatusOfEvent(response.id, 'active' , true).pipe(
          map((incident: any) => {
            if (!incident) return undefined;
            const orgId = this.arcAngelMonitorModeService.getOrgIdForLiveIncident(incident.id ?? '');
            this.illumLiveService.setManagingOrgId(orgId ?? this._managingOrgId);
            this.setManagingOrgId(orgId ?? this._managingOrgId);
            this.sessionOptionService.goToLiveIncidentMode(incident.id, true, true);

            return incident.id;
          })
        )
      ),
      catchError((error) => {
        console.error('Failed to create incident from perimeter.', error);
        return throwError(() => error);
      })
    );
  }
  updateStatusOfEvent(eventId: string, status: 'active' | 'inactive', isCampus?: Boolean): Observable<any> {
    const params: any = {
      organizationId: this._managingOrgId,
      pin: this.settingsService['_verifiedPin']
    };

    switch (status) {
      case 'active':
        if (isCampus) {
          return this.api.patch<any>(
            `${ConfigurationManagerService.settings.BaseURL}db/Incident/${eventId}/${ConfigurationManagerService.settings.ActivateCampusIncident}`,
            params
          );
        } else {
          return this.api.patch<any>(
            `${ConfigurationManagerService.settings.BaseURL}db/Incident/${eventId}/${ConfigurationManagerService.settings.Activate}`,
            params
          );
        }
       
      case 'inactive':
         let resolveCampus = false;
          if (Array.isArray(this._managingOrgId) && this._managingOrgId.length > 1) {
            resolveCampus = true;
          }
        if (resolveCampus) {
          return this.api.post<any>(
            `${ConfigurationManagerService.settings.BaseURL}db/Incident/${eventId}/${ConfigurationManagerService.settings.ResolveCampusIncident}`,
            { organizationId: this._managingOrgId }
          );
        } else {
          return this.api.post<any>(
            `${ConfigurationManagerService.settings.BaseURL}db/Incident/${eventId}/${ConfigurationManagerService.settings.ResolveIncident}`,
            { organizationId: this._managingOrgId }
          );
        }
      default:
        return new Observable(observer => observer.complete());
    }
  }

  /**
   * Create an incident with geofence and optional focal point.
   * @param incidentOptions The incident creation options.
   * @returns Observable<string> The created incident ID.
   */
  createIncident(incidentOptions: {
    coordinates: any;
    name: string;
    description: string;
    orgId: string;
    focalPoint?: any;
    orgIncidentCategoryId: string | number;
    public: boolean;
  }): Observable<string> {
    function flipLocationsNoZ(coords: any) {
      return coords.map((c: any) => Array.isArray(c) && c.length === 2 ? [c[0], c[1]] : c);
    }

    const locations = incidentOptions.coordinates;
    const label = incidentOptions.name;
    let description = incidentOptions.description || 'None';
    const orgId = incidentOptions.orgId;
    const focalPoint = incidentOptions.focalPoint;
    const orgIncidentCategoryId = incidentOptions.orgIncidentCategoryId;
    const isPublic = incidentOptions.public;

    const newIncident: any = {
      name: label,
      description: description,
      firstResponderOrgs: [],
      organizationIds: [orgId],
      safezones: [],
      Geofences: [{
        rating: 'HIGH',
        geometry: {
          type: 'Polygon',
          coordinates: [flipLocationsNoZ(locations)]
        }
      }],
      organizationIncidentCategoryId: orgIncidentCategoryId
    };

    if (focalPoint) {
      newIncident.focalPoint = focalPoint;
    }

    const apiToCall = isPublic
      ? ConfigurationManagerService.settings.CreateGeofencePublic
      :  ConfigurationManagerService.settings.CreateGeofence;
      
    const url = ConfigurationManagerService.settings.BaseURL + apiToCall;
    return this.api.post<any>(url, newIncident).pipe(
      map(response => response?.id || response?.data?.id),
      catchError(error => {
        console.error('Error creating incident', error);
        return throwError(() => error);
      })
    );
  }
  createSafeZone(eventId: any, name: any, description: any, lat: any, lon: any, perimeterSafezoneId?: any): Observable<any> {
    const payload = {
      label: name,
      description: description,
      active: false,
      geometry: {
        type: 'Point',
        coordinates: [lon, lat]
      },
      perimeterSafezoneId: perimeterSafezoneId !== undefined ? perimeterSafezoneId : undefined,
      incidentId: eventId
    };

    return this.createSafeZoneFromPayload(payload, true);
  }

  savePerimeterSafezone(perimeterSafezone: any): Observable<any> {
    const url = ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.PerimeterSafezoneFindAndCountAll;;
    return this.api.post<any>(url, perimeterSafezone);
  }

  saveSafezone(payload: any, autoActivate: boolean = true): Observable<any> {
    return this.createSafeZoneFromPayload(payload, autoActivate);
  }

  activateSafezone(safezoneId: string | number, orgId?: string | number, eventId?: string): Observable<any> {
    const cleanSafezoneId = String(safezoneId).replace(/^arc-angel-(playback-)?/, '');

    let resolvedOrgId = orgId ? String(orgId) : '';
    if (!resolvedOrgId && eventId && this.arcAngelMonitorModeService) {
      resolvedOrgId = this.arcAngelMonitorModeService.getOrgIdForLiveIncident(eventId) || '';
    }
    if (!resolvedOrgId && typeof localStorage !== 'undefined') {
      resolvedOrgId = localStorage.getItem('PAA_org') || '';
    }

    const baseUrl = ConfigurationManagerService.settings?.BaseURL;
    const endpoint = ConfigurationManagerService.settings?.Activate;
    const safeZoneEndpoint = ConfigurationManagerService.settings?.safeZone;
    const url = `${baseUrl}${safeZoneEndpoint}/${cleanSafezoneId}/${endpoint}`;

    const body = { organizationId: String(resolvedOrgId) };
    return this.api.patch<any>(url, body);
  }

  /**
   * Create a Safe Zone using a ready-made payload object.
   * Example payload structure:
   * { label, description, active, geometry: { type, coordinates }, incidentId, perimeterSafezoneId? }
   */
  createSafeZoneFromPayload(payload: any, autoActivate: boolean = true): Observable<any> {
    const baseUrl = ConfigurationManagerService.settings?.BaseURL;
    const safeZoneEndpoint = ConfigurationManagerService.settings?.safeZone;
    const url = `${baseUrl}${safeZoneEndpoint}`;

    return this.api.post<any>(url, payload).pipe(
      switchMap((response: any) => {
        const safezone = response?.data || response;
        const safezoneId = safezone?.id;
        if (autoActivate && safezoneId) {
          const incidentId = payload?.incidentId || safezone?.incidentId;
          const orgId = payload?.organizationId || safezone?.organizationId;
          return this.activateSafezone(safezoneId, orgId, incidentId).pipe(
            map((activateRes: any) => {
              return { ...safezone, active: true, isActive: true, activateResponse: activateRes };
            }),
            catchError((err: any) => {
              console.warn('Safezone created but activation failed:', err);
              return of(safezone);
            })
          );
        }
        return of(safezone);
      })
    );
  }
}